import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ProfileCard,
  type ProfileCardContract,
} from '../../../../../app/_shared/educationProfile';
import { paths, roles } from '../../../../../app/routes/paths';
import { useAppRole, useRolePath } from '../../../../../app/routes/useRolePath';
import { computeMentorProfileProgress } from '../../../../mentor/application/computeMentorProfileProgress';
import { useGetMentorMeQuery } from '../../../../mentor/infrastructure/api/mentor.api';
import { computeStudentProfileProgress } from '../../../../student/application/computeStudentProfileProgress';
import { useGetStudentMeQuery } from '../../../../student/infrastructure/api/student-profile.api';
import { useUserProfile } from '../../hooks/useUserProfile';
import { PictureByGender } from '../PictureByGender';

function ctaKey(state: 'empty' | 'partial' | 'complete'): string {
  if (state === 'complete') return 'dashboard.profileCard.cta_edit';
  if (state === 'partial') return 'dashboard.profileCard.cta_continue';
  return 'dashboard.profileCard.cta_fill';
}

type StudentProfileProps = {
  /** When false, hides the navigate-to-profile CTA (e.g. already on profile). */
  showCta?: boolean;
  /** Replaces default avatar preview (e.g. AvatarPicker edit mode). */
  pictureSlot?: ReactNode;
};

export const StudentProfile = ({
  showCta = true,
  pictureSlot,
}: StudentProfileProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const to = useRolePath();
  const role = useAppRole();
  const isMentor = role === roles.MENTOR;
  const { view, clockLabel, isLoading } = useUserProfile();

  const { data: mentor } = useGetMentorMeQuery(undefined, {
    skip: !isMentor,
  });
  const { data: student } = useGetStudentMeQuery(undefined, {
    skip: isMentor || role === roles.NONE,
  });

  const progress = isMentor
    ? computeMentorProfileProgress(mentor)
    : computeStudentProfileProgress(student);

  const displayName =
    (isMentor
      ? mentor?.profile?.displayName
      : student?.profile?.displayName) ||
    view?.displayName ||
    (isLoading ? '…' : '');

  const timezoneLabel =
    (isMentor ? mentor?.profile?.timezone : student?.profile?.timezone) ||
    clockLabel;

  const titleKey = isMentor
    ? 'dashboard.profileCard.mentor_title'
    : 'dashboard.profileCard.student_title';
  const hintKey = isMentor
    ? 'dashboard.profileCard.mentor_hint'
    : 'dashboard.profileCard.student_hint';
  const isPrimaryCta = progress.state !== 'complete';

  const contract: ProfileCardContract = {
    surface: 'dashboard',
    identity: {
      displayName,
      timezoneLabel,
      title: t(titleKey),
      avatarEdit: pictureSlot != null,
      picture:
        pictureSlot ?? (
          <div className="profile-wrapper__picture-avatar">
            <PictureByGender
              picture={
                (isMentor
                  ? mentor?.profile?.avatarUrl
                  : student?.profile?.avatarUrl) || view?.avatarUrl
              }
              gender={view?.gender}
            />
          </div>
        ),
    },
    progress: {
      percent: progress.percent,
      hint: progress.state === 'complete' ? null : t(hintKey),
      cta: showCta
        ? {
            label: t(ctaKey(progress.state)),
            primary: isPrimaryCta,
            onClick: () => navigate(to(paths.profile)),
          }
        : null,
    },
  };

  return <ProfileCard contract={contract} />;
};

export default StudentProfile;
